// UX roadmap #58: what the member's background content syncs last reported, and how the connection banner reads it.
// Pure functions: the Application store keeps the state, ConnectionBanner.vue renders it.

/**
 * The BroadcastChannel the tabs share sync news on (UX roadmap #18). Cooldowns live in the shared IndexedDB, so a tab
 * whose own worker finds the rows already written reports nothing; the tab whose worker wrote them relays here.
 */
export const SYNC_BROADCAST_CHANNEL = 'tmi-sync' as const;

/** How old the last clean sync of a key that keeps failing may get before the banner says content may be out of date. */
export const SyncStaleAfterMilliseconds: number = 15 * 60_000;

/** How long a failure streak lasts before it counts: one failed pass is retried in 15 s and must not flash a banner. */
export const SyncStaleGraceMilliseconds: number = 60_000;

export interface SyncKeyHealth {
    /** False while the key's syncs are failing. */
    ok: boolean;
    /** When the key last synced cleanly on this device (epoch ms), null if that is not known. */
    lastOkAt: number | null;
    /** When the current failure streak began (epoch ms); null while ok. */
    failingSince: number | null;
}

export type SyncHealth = Readonly< Record< string, SyncKeyHealth > >;

/** A MessageTypes.SYNC_STATUS report: `ok` absent when the key's sync did not run (no verdict). */
export interface SyncStatusReport {
    ok?: boolean;
    at?: number | null;
}

function newest( a: number | null, b: number | null ): number | null {
    if ( null == a ) return b;
    if ( null == b ) return a;
    return Math.max( a, b );
}

/** The key's health after `report`, received at `now`. */
export function nextSyncKeyHealth( previous: SyncKeyHealth | undefined, report: SyncStatusReport, now: number ): SyncKeyHealth {
    const lastOkAt = newest( previous?.lastOkAt ?? null, 'number' === typeof report.at ? report.at : null );

    if ( true === report.ok ) {
        return { ok: true, lastOkAt, failingSince: null };
    }
    if ( false === report.ok ) {
        return { ok: false, lastOkAt, failingSince: previous?.failingSince ?? now };
    }

    // No verdict, only the shared stamp: a clean sync since the streak began (another tab's) ends the streak.
    if ( previous && !previous.ok && null != previous.failingSince && null != lastOkAt && lastOkAt >= previous.failingSince ) {
        return { ok: true, lastOkAt, failingSince: null };
    }
    return { ok: previous?.ok ?? true, lastOkAt, failingSince: previous?.failingSince ?? null };
}

/** The oldest of the keys' last clean syncs: everything shown is at least this fresh. Null when none is known. */
export function lastSyncedAt( health: SyncHealth ): number | null {
    let oldest: number | null = null;
    for ( const { lastOkAt } of Object.values( health ) ) {
        if ( null != lastOkAt && ( null == oldest || lastOkAt < oldest ) ) {
            oldest = lastOkAt;
        }
    }
    return oldest;
}

/**
 * Whether some key's syncs have been failing for over a minute and its content is over SyncStaleAfterMilliseconds
 * old (counted from the start of the streak when this device never synced it cleanly).
 */
export function isSyncStale( health: SyncHealth, now: number ): boolean {
    return Object.values( health ).some( ( { ok, lastOkAt, failingSince } ) =>
        !ok && null != failingSince
        && now - failingSince >= SyncStaleGraceMilliseconds
        && now - ( lastOkAt ?? failingSince ) >= SyncStaleAfterMilliseconds );
}

/** "just now", "12 min ago", "3 hours ago", "2 days ago". */
export function formatSyncedAgo( at: number, now: number ): string {
    const minutes = Math.floor( ( now - at ) / 60_000 );
    if ( minutes < 1 ) {
        return 'just now';
    }
    if ( minutes < 60 ) {
        return `${ minutes } min ago`;
    }
    const hours = Math.floor( minutes / 60 );
    if ( hours < 24 ) {
        return 1 === hours ? '1 hour ago' : `${ hours } hours ago`;
    }
    const days = Math.floor( hours / 24 );
    return 1 === days ? '1 day ago' : `${ days } days ago`;
}
